import {requirePermission} from '@/modules/auth/permissions'
import {useGetInfiniteUsers} from '@/modules/users/api'
import {UsersFilters, UsersTable} from '@/modules/users/components'
import {UsersSearchSchema} from '@/modules/users/schemas'
import {LinkButton} from '@cypro/ui/components/ui/link-button'
import {ButtonGroup} from '@chakra-ui/react'
import {createFileRoute} from '@tanstack/react-router'
import {type OnChangeFn, type SortingState} from '@tanstack/react-table'
import {useTranslate} from '@tolgee/react'
import {useCallback} from 'react'
import {LuPlus} from 'react-icons/lu'
import {ResourcesIndexPageTable} from '@/shared/components/ui/resources-index-page-table'

const LIMIT = 50

export const Route = createFileRoute('/configuration/users/')({
  beforeLoad: requirePermission('users.manage'),
  component: UsersPage,
  validateSearch: UsersSearchSchema,
})

function UsersPage() {
  const {t} = useTranslate('common')
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const [sort] = search.sort

  const infinite = useGetInfiniteUsers({
    limit: LIMIT,
    sort: {
      dir: sort?.desc ? 'desc' : 'asc',
      field: sort?.id || 'createdAt',
    },
    filters: {
      name: search.filters?.search,
      groups: search.filters?.groups,
      sections: search.filters?.sections,
      wards: search.filters?.wards,
      status: [],
    },
  })

  const {getPageQuery, getSingleQuery, toStartRow, listContextParams} = infinite

  const onSortingChange = useCallback<OnChangeFn<SortingState>>(
    updaterOrValue => {
      const newValue = typeof updaterOrValue === 'function' ? updaterOrValue(search.sort) : updaterOrValue
      navigate({to: '.', search: prev => ({...prev, sort: newValue})})
    },
    [navigate, search.sort]
  )

  return (
    <>
      <ButtonGroup size="sm" display="flex" width="100%" marginY="1rem">
        <LinkButton to="/configuration/users/new" colorPalette="cyan">
          <LuPlus />
          {t('add_new')}
        </LinkButton>

        <UsersFilters />
      </ButtonGroup>

      <ResourcesIndexPageTable>
        <UsersTable
          virtualizer={{getPageQuery, getSingleQuery, toStartRow, listContextParams}}
          table={{onSortingChange, state: {sorting: search.sort}}}
        />
      </ResourcesIndexPageTable>
    </>
  )
}
